import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FEATURE_MODULES, CORE_MODULE_KEYS } from "../../config/feature-modules";
import { BUSINESS_MODELS } from "../../config/business-models";
import { organizationRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, PageHead, Switch } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function ModuleManager() {
  const { organization, enabledModules, setModules, refreshOrganizations, toast, businessModel, plan, planModules, moduleLocked, upgradeFor } = useApp();
  const { tx, pick } = useI18n();
  const [selected, setSelected] = useState(enabledModules);
  const [saving, setSaving] = useState(false);

  useEffect(() => setSelected(enabledModules), [enabledModules]);

  const toggle = (key) => {
    if (CORE_MODULE_KEYS.includes(key)) return;
    // โมดูลนอกแพ็กเกจเปิดไม่ได้ ต้องอัปเกรดก่อน
    if (moduleLocked(key)) {
      const needed = upgradeFor(key);
      toast(
        tx(`โมดูลนี้อยู่ในแพ็กเกจ ${needed ? needed.name : "ที่สูงกว่า"} กรุณาอัปเกรดก่อน`, `Available on ${needed ? needed.nameEn : "a higher"} plan`),
        "error"
      );
      return;
    }
    setSelected((rows) => (rows.includes(key) ? rows.filter((row) => row !== key) : [...rows, key]));
  };

  const save = async () => {
    setSaving(true);
    await setModules(selected);
    setSaving(false);
    toast(tx("บันทึกโมดูลเรียบร้อย เมนูถูกอัปเดตแล้ว", "Modules saved, menu updated"), "success");
  };

  const applyPreset = async (model) => {
    // preset เปิดได้เฉพาะโมดูลที่แพ็กเกจครอบคลุม ที่เหลือแจ้งว่าต้องอัปเกรด
    const allowed = model.modules.filter((key) => planModules.includes(key));
    const skipped = model.modules.length - allowed.length;

    await organizationRepo.update(null, organization.id, { businessModel: model.key, enabledModules: allowed });
    await refreshOrganizations();

    toast(
      skipped
        ? tx(
            `เปลี่ยนเป็นโมเดล ${model.name} แล้ว — อีก ${skipped} โมดูลต้องอัปเกรดแพ็กเกจ`,
            `Switched to ${model.nameEn} — ${skipped} more modules need an upgrade`
          )
        : tx(`เปลี่ยนเป็นโมเดล ${model.name} แล้ว`, `Switched to ${model.nameEn}`),
      skipped ? "" : "success"
    );
  };

  return (
    <>
      <PageHead
        title={tx("จัดการโมดูลธุรกิจ", "Module manager")}
        subtitle={tx("เปิด-ปิดความสามารถให้ตรงกับธุรกิจ เมนูและหน้าจอจะปรับตามอัตโนมัติ", "Toggle capabilities to match your business — menus adapt automatically")}
        actions={
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? tx("กำลังบันทึก...", "Saving...") : tx("บันทึกการตั้งค่า", "Save settings")}
          </Button>
        }
      />

      {/* <Card
        title={tx("ฐานข้อมูล Google Sheets", "Google Sheets database")}
        subtitle={
          gsEnabled()
            ? tx("โมดูล CRM ทั้งหมดอ่าน-เขียนที่ Google Sheets", "All CRM modules read/write to Google Sheets")
            : tx("ยังไม่ได้ตั้งค่า — ตอนนี้เก็บข้อมูลในเครื่อง (localStorage)", "Not configured — using local storage")
        }
        actions={
          <>
            <Button
              onClick={async () => {
                setGsBusy("ping");
                try {
                  const result = await gsPing();
                  toast(tx(`เชื่อมต่อสำเร็จ (${result.tables} ตาราง)`, `Connected (${result.tables} tables)`), "success");
                } catch (err) {
                  toast(err.message, "error");
                } finally {
                  setGsBusy("");
                }
              }}
              disabled={!gsEnabled() || !!gsBusy}
            >
              {gsBusy === "ping" ? tx("กำลังเช็ค...", "Checking...") : tx("ทดสอบการเชื่อมต่อ", "Test connection")}
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                setGsBusy("sync");
                try {
                  const result = await syncSeedToGs();
                  const total = Object.values(result || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
                  toast(tx(`ซิงก์ข้อมูลขึ้น Sheets แล้ว ${total} แถว`, `Synced ${total} rows to Sheets`), "success");
                  setTimeout(() => window.location.reload(), 800);
                } catch (err) {
                  toast(err.message, "error");
                } finally {
                  setGsBusy("");
                }
              }}
              disabled={!gsEnabled() || !!gsBusy}
            >
              {gsBusy === "sync" ? tx("กำลังซิงก์...", "Syncing...") : tx("ซิงก์ข้อมูลตัวอย่างขึ้น Sheets", "Sync demo data to Sheets")}
            </Button>
          </>
        }
      >
        <div className="u-fs-13 u-muted">
          {gsEnabled() ? (
            <>
              <div className="u-mb-8">
                <Badge tone="green">{tx("เชื่อมต่อแล้ว", "Connected")}</Badge>{" "}
                <span style={{ wordBreak: "break-all" }}>{GS_URL}</span>
              </div>
              {tx(
                "สมัครสมาชิก/เข้าสู่ระบบ และการสร้าง QR ยังใช้ PaymentBroker เหมือนเดิม แต่จะบันทึกสำเนาลงชีต users และ transactions ด้วย",
                "Auth and QR payment still use PaymentBroker, with a mirror copy written to the users and transactions sheets."
              )}
            </>
          ) : (
            tx(
              "ตั้งค่า REACT_APP_GS_URL และ REACT_APP_GS_KEY ใน .env แล้วรีสตาร์ท dev server (ดูวิธีติดตั้งที่ gs/README.md)",
              "Set REACT_APP_GS_URL and REACT_APP_GS_KEY in .env then restart the dev server (see gs/README.md)."
            )
          )}
        </div>
      </Card> */}

      <Card
        title={tx("โมเดลธุรกิจสำเร็จรูป", "Business presets")}
        subtitle={tx("เลือกเพื่อตั้งค่าโมดูลอัตโนมัติ ปรับเพิ่มลดทีหลังได้", "Pick a preset then fine-tune below")}
      >
        <div className="grid-4" style={{ gap: 10 }}>
          {BUSINESS_MODELS.map((model) => (
            <button
              key={model.key}
              type="button"
              className={`module-card ${organization?.businessModel === model.key ? "is-on" : ""}`}
              style={{ cursor: "pointer", textAlign: "left" }}
              onClick={() => applyPreset(model)}
            >
              <span className="module-card__icon"><Icon name={model.icon} /></span>
              <span style={{ minWidth: 0 }}>
                <span className="u-strong u-fs-13" style={{ display: "block" }}>{pick(model)}</span>
                <span className="u-fs-12 u-muted">{model.modules.length} {tx("โมดูล", "modules")}</span>
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card
        title={tx("ความสามารถทั้งหมด", "All capabilities")}
        subtitle={tx(
          `ธุรกิจปัจจุบัน: ${businessModel.name} · แพ็กเกจ ${plan ? plan.name : "-"} เปิดได้ ${planModules.length} จาก ${FEATURE_MODULES.length} โมดูล`,
          `Current: ${businessModel.nameEn} · ${plan ? plan.nameEn : "-"} plan unlocks ${planModules.length} of ${FEATURE_MODULES.length} modules`
        )}
      >
        <div className="grid-3" style={{ gap: 10 }}>
          {FEATURE_MODULES.map((module) => {
            const isCore = CORE_MODULE_KEYS.includes(module.key);
            const locked = moduleLocked(module.key);
            const isOn = !locked && selected.includes(module.key);
            const needed = locked ? upgradeFor(module.key) : null;
            return (
              <div
                key={module.key}
                className={`module-card ${isOn ? "is-on" : ""}`}
                style={locked ? { opacity: 0.6 } : undefined}
              >
                <span className="module-card__icon"><Icon name={locked ? "lock" : module.icon} /></span>
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div className="u-between">
                    <span className="u-strong u-fs-13">{pick(module)}</span>
                    {isCore ? <Badge tone="gray">{tx("หลัก", "Core")}</Badge> : null}
                  </div>
                  <div className="u-fs-12 u-muted u-mb-8">{module.detail}</div>
                  {locked ? (
                    <Link to="/admin/subscription" className="u-fs-12">
                      <Icon name="lock" /> {tx(`ต้องใช้แพ็กเกจ ${needed ? needed.name : "ที่สูงกว่า"}`, `Needs ${needed ? needed.nameEn : "a higher"} plan`)}
                    </Link>
                  ) : !isCore ? (
                    <Switch checked={isOn} onChange={() => toggle(module.key)} />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}
